import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumberCompact, formatCurrencyUSD, formatPercent } from '../../utils/format';
import { useSettings } from '../../contexts/SettingsContext';
import { useFormatting } from '../../hooks/useFormatting';
import ChartExportButton from './ChartExportButton';
import '../../styles/AdminStyles.css';

/**
 * Area Chart Component
 * Displays time-series data with filled area under the curve
 * Great for showing trends and cumulative values over time
 */
const AreaChart = ({ 
  data = [], 
  comparisonData = [],
  label = '',
  formatType = 'number',
  color = 'accent',
  stacked = false,
  stackData = [],
  showExport = true,
  exportFilename = 'chart'
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [isCompact, setIsCompact] = useState(false);
  const chartContainerRef = useRef(null);
  const svgRef = useRef(null);

  // Get settings from context
  const settings = useSettings();
  const { formatDate } = useFormatting();

  // Check if inside compact container
  useEffect(() => {
    if (chartContainerRef.current) {
      const hasCompactParent = chartContainerRef.current.closest('.chart-container-compact') !== null;
      setIsCompact(hasCompactParent);
    }
  }, []);

  // Get user preference for chart animations from settings
  const animationsEnabled = settings.chartAnimations;
  
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const width = isCompact ? 700 : 1000;
    const height = isCompact ? 300 : 400;
    const padding = { top: 40, right: 60, bottom: 60, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Normalize data values
    const allValues = [
      ...data.map(d => typeof d.value === 'number' ? d.value : 0),
      ...comparisonData.map(d => typeof d.value === 'number' ? d.value : 0),
      ...(stacked && stackData.length > 0 ? stackData.flatMap(s => s.map(d => typeof d.value === 'number' ? d.value : 0)) : [])
    ];
    const minValue = Math.min(...allValues.filter(v => !isNaN(v) && isFinite(v)), 0);
    const maxValue = Math.max(...allValues.filter(v => !isNaN(v) && isFinite(v)), minValue || 1);

    const stepX = data.length > 1 ? chartWidth / (data.length - 1) : 0;

    const normalize = (value) => {
      if (maxValue === minValue) return chartHeight / 2;
      return chartHeight - ((value - minValue) / (maxValue - minValue)) * chartHeight;
    };

    // Generate area path
    const generateAreaPath = (points) => {
      if (points.length === 0) return '';
      if (points.length === 1) {
        const y = normalize(points[0].value);
        return `M ${padding.left} ${padding.top + y} L ${padding.left} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;
      }
      
      const topPath = points.map((point, index) => {
        const x = padding.left + (index * stepX);
        const y = padding.top + normalize(point.value);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      }).join(' ');
      
      const bottomPath = `L ${padding.left + chartWidth} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;
      
      return topPath + ' ' + bottomPath;
    };

    // Generate line path
    const generateLinePath = (points) => {
      if (points.length === 0) return '';
      if (points.length === 1) {
        const y = normalize(points[0].value);
        return `M ${padding.left} ${padding.top + y} L ${padding.left} ${padding.top + y}`;
      }
      return points.map((point, index) => {
        const x = padding.left + (index * stepX);
        const y = padding.top + normalize(point.value);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      }).join(' ');
    };

    const currentPoints = data.map((d, index) => ({
      value: typeof d.value === 'number' ? d.value : 0,
      date: d.date || d.label || `${index}`,
      index
    }));

    const comparisonPoints = comparisonData.map((d, index) => ({
      value: typeof d.value === 'number' ? d.value : 0,
      date: d.date || d.label || `${index}`,
      index
    }));

    const currentAreaPath = generateAreaPath(currentPoints);
    const currentLinePath = generateLinePath(currentPoints);
    const comparisonAreaPath = comparisonData.length > 0 ? generateAreaPath(comparisonPoints) : '';
    const comparisonLinePath = comparisonData.length > 0 ? generateLinePath(comparisonPoints) : '';

    return {
      currentAreaPath,
      currentLinePath,
      comparisonAreaPath,
      comparisonLinePath,
      currentPoints,
      comparisonPoints,
      minValue,
      maxValue,
      width,
      height,
      padding,
      chartWidth,
      chartHeight
    };
  }, [data, comparisonData, stacked, stackData, isCompact]);

  const formatValue = (value) => {
    switch (formatType) {
      case 'currency':
        return formatCurrencyUSD(value);
      case 'percent':
        return formatPercent(value);
      case 'number':
      default:
        return formatNumberCompact(value);
    }
  };

  const handlePointHover = (point, isComparison = false, event) => {
    if (!chartContainerRef.current || !svgRef.current || !chartData) return;
    
    const svgRect = svgRef.current.getBoundingClientRect();
    const stepX = chartData.chartWidth / Math.max(1, chartData.currentPoints.length - 1);
    const x = chartData.padding.left + (point.index * stepX);
    const y = chartData.padding.top + (chartData.chartHeight - ((point.value - chartData.minValue) / (chartData.maxValue - chartData.minValue || 1)) * chartData.chartHeight);
    
    const comparisonPoint = !isComparison && comparisonData.length > 0 
      ? chartData.comparisonPoints.find(p => p.index === point.index) || null
      : null;
    
    const svgX = (x / chartData.width) * svgRect.width;
    const svgY = (y / chartData.height) * svgRect.height;
    
    let growthPercent = null;
    if (comparisonPoint && point.value > 0 && comparisonPoint.value > 0) {
      growthPercent = ((point.value - comparisonPoint.value) / comparisonPoint.value) * 100;
    }
    
    setHoveredPoint({
      current: point,
      comparison: comparisonPoint,
      isComparison,
      growthPercent,
      x: svgX,
      y: svgY
    });
  };

  if (!chartData || data.length === 0) {
    return (
      <div className="chart-container chart-empty">
        <div className="chart-label">{label}</div>
        <div className="chart-placeholder">No data available</div>
      </div>
    );
  }

  const strokeColor = '#53EF97';
  const comparisonColor = '#2e8b57';

  return (
    <div 
      className="chart-container area-chart" 
      ref={chartContainerRef}
      onMouseLeave={() => setHoveredPoint(null)}
    >
      <div className="chart-header">
        {label && <div className="chart-label">{label}</div>}
        {showExport && (
          <ChartExportButton 
            chartRef={chartContainerRef}
            filename={exportFilename || label || 'area-chart'}
            formats={['png', 'svg', 'pdf']}
          />
        )}
      </div>
      <svg 
        ref={svgRef}
        className="chart-svg area-chart-svg" 
        viewBox={`0 0 ${chartData.width} ${chartData.height}`}
        preserveAspectRatio="none"
        aria-label={label || 'Area Chart'}
      >
        <defs>
          <linearGradient id={`area-gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.5" />
            <stop offset="50%" stopColor={strokeColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.1" />
          </linearGradient>
          {comparisonData.length > 0 && (
            <linearGradient id={`area-gradient-comparison-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={comparisonColor} stopOpacity="0.3" />
              <stop offset="50%" stopColor={comparisonColor} stopOpacity="0.2" />
              <stop offset="100%" stopColor={comparisonColor} stopOpacity="0.05" />
            </linearGradient>
          )}
        </defs>

        {/* Grid lines */}
        {Array.from({ length: 6 }).map((_, i) => {
          const y = chartData.padding.top + (i * chartData.chartHeight / 5);
          return (
            <line
              key={`grid-h-${i}`}
              x1={chartData.padding.left}
              y1={y}
              x2={chartData.padding.left + chartData.chartWidth}
              y2={y}
              stroke="var(--admin-card-border)"
              strokeWidth="0.5"
              opacity="0.2"
              strokeDasharray="2 2"
            />
          );
        })}

        {/* Comparison area (behind) */}
        {chartData.comparisonAreaPath && (
          <path
            d={chartData.comparisonAreaPath}
            fill={`url(#area-gradient-comparison-${color})`}
            className="area-chart-area area-chart-area-comparison"
          />
        )}

        {/* Current area */}
        <path
          d={chartData.currentAreaPath}
          fill={`url(#area-gradient-${color})`}
          className="area-chart-area area-chart-area-current"
        />

        {/* Comparison line */}
        {chartData.comparisonLinePath && (
          <path
            d={chartData.comparisonLinePath}
            fill="none"
            stroke={comparisonColor}
            strokeWidth="2"
            strokeDasharray="4 3"
            strokeOpacity="0.7"
            className="area-chart-line area-chart-line-comparison"
          />
        )}

        {/* Current line */}
        <path
          d={chartData.currentLinePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth="2.5"
          className="area-chart-line area-chart-line-current"
        />

        {/* Data points */}
        {chartData.currentPoints.map((point, index) => {
          const stepX = chartData.chartWidth / Math.max(1, chartData.currentPoints.length - 1);
          const x = chartData.padding.left + (index * stepX);
          const y = chartData.padding.top + (chartData.chartHeight - ((point.value - chartData.minValue) / (chartData.maxValue - chartData.minValue || 1)) * chartData.chartHeight);
          const isHovered = hoveredPoint?.current?.index === index && !hoveredPoint?.isComparison;
          
          return (
            <g key={`point-${index}`}>
              <circle
                cx={x}
                cy={y}
                r="8"
                fill="transparent"
                className="area-chart-point-hover"
                onMouseEnter={(e) => handlePointHover(point, false, e)}
                style={{ cursor: 'pointer' }}
              />
              <circle
                cx={x}
                cy={y}
                r={isHovered ? "5" : "3"}
                fill={strokeColor}
                stroke="#000B04"
                strokeWidth={isHovered ? "2" : "1"}
                className={`area-chart-point ${isHovered ? 'hovered' : ''}`}
              />
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      {(chartData.comparisonLinePath && chartData.currentLinePath) && (
        <div className="chart-legend">
          <div className="chart-legend-item">
            <span className="chart-legend-line chart-legend-current"></span>
            <span>Current Period</span>
          </div>
          <div className="chart-legend-item">
            <span className="chart-legend-line chart-legend-comparison"></span>
            <span>Previous Period</span>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {settings.showTooltips && hoveredPoint && chartContainerRef.current && svgRef.current && (
        <div 
          className="chart-tooltip area-chart-tooltip"
          style={{
            left: `${hoveredPoint.x}px`,
            top: `${hoveredPoint.y - 100}px`,
            transform: hoveredPoint.x > (svgRef.current.clientWidth * 0.7) ? 'translateX(-100%)' : 'translateX(-50%)'
          }}
        >
          <div className="chart-tooltip-header">
            <div className="chart-tooltip-date">
              {(() => {
                const date = hoveredPoint.current.date;
                if (date instanceof Date) {
                  // Use user preference for date formatting
                  return formatDate(date) || date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                }
                return date || hoveredPoint.current.label || 'N/A';
              })()}
            </div>
          </div>
          <div className="chart-tooltip-content">
            <div className="chart-tooltip-item chart-tooltip-current">
              <div className="chart-tooltip-label">Current Period</div>
              <div className="chart-tooltip-value">{formatValue(hoveredPoint.current.value)}</div>
            </div>
            {hoveredPoint.comparison && (
              <div className="chart-tooltip-item chart-tooltip-comparison">
                <div className="chart-tooltip-label">Previous Period</div>
                <div className="chart-tooltip-value">{formatValue(hoveredPoint.comparison.value)}</div>
              </div>
            )}
            {hoveredPoint.growthPercent !== null && hoveredPoint.comparison && (
              <div className="chart-tooltip-growth">
                <i className={`bi ${hoveredPoint.growthPercent >= 0 ? 'bi-arrow-up' : 'bi-arrow-down'} icon-brand`}></i>
                <span className={hoveredPoint.growthPercent >= 0 ? 'positive' : 'negative'}>
                  {formatPercent(Math.abs(hoveredPoint.growthPercent), 2)}
                </span>
                <span className="chart-tooltip-growth-label">change</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

AreaChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      value: PropTypes.number.isRequired,
      label: PropTypes.string
    })
  ).isRequired,
  comparisonData: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      value: PropTypes.number.isRequired,
      label: PropTypes.string
    })
  ),
  label: PropTypes.string,
  formatType: PropTypes.oneOf(['number', 'currency', 'percent']),
  color: PropTypes.string,
  stacked: PropTypes.bool,
  stackData: PropTypes.array,
  showExport: PropTypes.bool,
  exportFilename: PropTypes.string
};

export default AreaChart;
